import React from 'react';
import type { ColDef, ColGroupDef, ICellRendererParams, IRichCellEditorParams, ValueGetterParams } from 'ag-grid-enterprise';
import type { AppState } from 'admin/src/store';
import { createSelector } from '@reduxjs/toolkit';
import type { ModuleInfoDisplayState, PollworkerModuleInfo, CustomField, PollworkerUserInfo } from 'admin/src/types';
import { WorkerLevelById } from 'admin/src/components/enums/WorkerLevelEnum';
import CellIconWrapper from 'admin/src/components/Pollworker/utils/CellIconWrapper';
import StatusCellRenderer, { workerStatusContent } from 'admin/src/components/Pollworker/utils/CellStatusRenderer';
import { Flexor } from 'shared/src/components';
import { dateFormatter, phoneNumberFormatter } from 'shared/src/utils/formatters';
import type { Party, PollworkerWorkHistory, Precinct, UserInfo } from 'admin/src/types';
import dayjs from 'dayjs';
import { STATES_BY_ABBR } from 'shared/src/utils/data/usStates';
import validator from 'validator';
import type { CustomerInfo } from 'shared/src/types';
import { PARTY_COLORS } from 'admin/src/screens/admin/Pollworker/StaffingChart/staffingTypes';
import { generateHslColor } from 'shared/src/utils/colors';
import type { PartyConfig } from 'admin/src/screens/admin/Pollworker/StaffingChart/staffingTypes';
import type { PickerWorker } from 'admin/src/screens/admin/Pollworker/StaffingChart';

export const moduleInfoSelector = (state: AppState) => state.pollworker.moduleInfo;
const precinctsSelector = (state: AppState) => state.pollworker.grid.precincts;
export const partiesSelector = (state: AppState) => state.pollworker.grid.parties;
export const pollworkers = (state: AppState) => state.pollworker.grid.pollworkers;
export const getReportsTo = (state: AppState) => state.pollworker.grid.reportsTo;
export const getCustomFields = (state: AppState) => state.pollworker.grid.customFields;

export const getReportsToByIdSelector = createSelector([getReportsTo], (reportsTo) => {
  return reportsTo.reduce((acc: Record<string, PollworkerUserInfo>, rt: PollworkerUserInfo) => {
    acc[rt.id] = { ...rt, _fullName: `${rt.firstName} ${rt.lastName}` };
    return acc;
  }, {});
});

export const getCustomFieldsByPromptText = createSelector([getCustomFields], (customFields) => {
  return customFields.reduce((acc: Record<string, CustomField>, cf: CustomField) => {
    acc[cf.promptText] = cf;
    return acc;
  }, {});
});

export const getCustomFieldsByPromptTextSelector = createSelector([getCustomFieldsByPromptText], (customFieldsByPromptText) => customFieldsByPromptText);

export const getCustomFieldsByPromptTextKeysSelector = createSelector([getCustomFieldsByPromptTextSelector], (customFieldsByPromptText) => Object.keys(customFieldsByPromptText));

export const displayStatesByIdSelector = createSelector([moduleInfoSelector], (moduleInfo) => {
  if (!moduleInfo) return;

  return moduleInfo.DisplayStates?.reduce((acc: { [key: string]: ModuleInfoDisplayState }, state: ModuleInfoDisplayState) => {
    acc[state.id] = state;
    return acc;
  }, {});
});

export const displayStatesByNameSelector = createSelector([moduleInfoSelector], (moduleInfo) => {
  if (!moduleInfo) return;

  return moduleInfo.DisplayStates?.reduce((acc: { [key: string]: ModuleInfoDisplayState }, state: ModuleInfoDisplayState) => {
    acc[state.StateName] = state;
    return acc;
  }, {});
});

const selectedRowsPollworkerIds = (state: AppState) => state.pollworker.grid.selectedRowsPollworkerIds;
export const getSelectedPollworkers = createSelector([selectedRowsPollworkerIds, pollworkers], (selectedIds, pollworkers) => {
  const pollworkersById = pollworkers.workerList.reduce((acc, pw: PollworkerWorkHistory): any => {
    return { ...acc, [pw.id]: pw };
  }, {});

  return selectedIds.map((id: string) => {
    // @ts-ignore
    return pollworkersById[id];
  });
});

function getRowDisplayState(params: any, displayStatesById?: { [p: string]: ModuleInfoDisplayState }) {
  if (!displayStatesById) return;

  const row = params.node;
  if (!displayStatesById || !row || !row.data || !row.data.pollworkerInfo.displayStateId) return;

  return displayStatesById[row.data.pollworkerInfo.displayStateId];
}

export const agGridColDefsSelector = createSelector(
  partiesSelector,
  precinctsSelector,
  getReportsTo,
  moduleInfoSelector,
  getCustomFieldsByPromptTextKeysSelector,
  getCustomFieldsByPromptTextSelector,
  getReportsToByIdSelector,
  (state: AppState) => state.user.account,
  displayStatesByIdSelector,
  displayStatesByNameSelector,
  (
    parties: Party[],
    precincts: Precinct[],
    reportsTo: UserInfo[],
    moduleInfo: PollworkerModuleInfo | null,
    customFieldsByName: string[],
    customFieldsByPromptText: any,
    reportsToById: any,
    account: CustomerInfo | undefined,
    displayStatesById: undefined | { [p: string]: ModuleInfoDisplayState },
    displayStatesByName: undefined | { [p: string]: ModuleInfoDisplayState },
  ): (ColDef | ColGroupDef)[] => {
    return [
      {
        field: 'pollworkerInfo.userInfo.fullName',
        pinned: 'left',
        valueGetter: (params: ValueGetterParams) =>
          params.node?.group ? null : `${params.data.pollworkerInfo.userInfo.firstName} ${params.data.pollworkerInfo.userInfo.lastName}`,
        filter: true,
        enableCellChangeFlash: true,
        headerName: 'Full Name',
      },
      {
        headerName: 'Flag',
        width: 40,
        pinned: 'left',
        field: 'static',
        filter: true,
        filterParams: {
          cellRenderer: (params: any) => {
            if (!displayStatesByName) return;

            const displayState = displayStatesByName[params.value];
            if (!displayState) return params.value || '(Blanks)';

            return (
              <Flexor className="space-x-1">
                <div className="h-5 w-5" style={{ backgroundColor: `#${displayState.BackColor}` }} />
                <div>{displayState.StateName}</div>
              </Flexor>
            );
          },
        },
        filterValueGetter: (params: any) => {
          const displayState = getRowDisplayState(params, displayStatesById);
          if (!displayState) return params.value;

          return displayState.StateName;
        },
        tooltipValueGetter: (params: any) => {
          const displayState = getRowDisplayState(params, displayStatesById);
          if (!displayState) return;

          return displayState.StateName;
        },
        cellStyle: (params: any) => {
          const displayState = getRowDisplayState(params, displayStatesById);
          if (!displayState) return;

          return {
            background: `#${displayState.BackColor}`,
          };
        },
      },
      {
        field: 'pollworkerInfo.status',
        pinned: 'left',
        lockPosition: true,
        headerName: 'Status',
        width: 70,
        suppressColumnsToolPanel: true,
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
        getQuickFilterText: (params: any) => {
          return workerStatusContent(Number(params.value))?.content;
        },
        valueFormatter: (params: any) => {
          const { content } = workerStatusContent(Number(params.value));
          return content;
        },
        filterParams: {
          cellRenderer: (params: ICellRendererParams) => {
            if (typeof params.value === 'string') return params.value;

            return (
              <CellIconWrapper>
                <StatusCellRenderer params={params} filterDisplay />
              </CellIconWrapper>
            );
          },
        },
        cellRenderer: (params: ICellRendererParams) => (
          <CellIconWrapper>
            <StatusCellRenderer params={params} />
          </CellIconWrapper>
        ),
      },
      {
        colId: 'workerLevelKey',
        field: 'workerLevel',
        headerName: 'Level',
        filter: true,
        width: 100,
        filterValueGetter: (params: any) => {
          return WorkerLevelById[params.data.workerLevel]?.displayName;
        },
        valueGetter: (params: ValueGetterParams) => {
          if (params.node?.group) return null;

          return WorkerLevelById[params.data.workerLevel]?.displayName;
        },
      },
      {
        field: 'pollworkerInfo.userInfo.userId',
        filter: true,
        headerName: 'User ID',
        editable: false,
        width: 100,
      },
      {
        field: 'pollworkerInfo.userInfo.okToSendSMS',
        filter: true,
        headerName: 'SMS?',
        editable: true,
        enableRowGroup: true,
        valueFormatter: (params: any) => {
          return params.value ? 'Yes' : 'No';
        },
        width: 80,
        context: {
          update: { type: 'userInfo' },
        },
      },
      {
        field: 'pollworkerInfo.electionDayWorker',
        editable: true,
        enableRowGroup: true,
        width: 156,
        context: {
          update: { type: 'pollworkerInfo' },
        },
        filter: true,
        headerName: 'Election Day Worker',
      },
      {
        field: 'pollworkerInfo.earlyVotingWorker',
        context: {
          update: { type: 'pollworkerInfo' },
        },
        editable: true,
        width: 150,
        filter: true,
        enableRowGroup: true,
        headerName: 'Early Voting Worker',
      },
      {
        field: '_workAssignmentConfirmation.value',
        filter: true,
        editable: true,
        headerName: 'Work Confirmation',
        context: {
          update: {
            type: 'workConfirmation',
          },
        },
        valueFormatter: (params: any) => {
          if (params.value === 1) return 'Accepted';
          if (params.value === 2) return 'Declined';
          return 'No Response';
        },
        cellEditorSelector: () => ({
          component: 'agRichSelectCellEditor',
          params: {
            cellRenderer: (params: any) => {
              return (
                <div className="">
                  {params.value === 0 ? 'Reset' : null}
                  {params.value === 1 ? 'Accept' : null}
                  {params.value === 2 ? 'Decline' : null}
                </div>
              );
            },
            values: [0, 1, 2],
          },
          popup: true,
        }),
      },
      {
        colId: 'electionDay',
        headerName: 'Election Day',
        children: [
          {
            field: 'electionDayRole.roleName',
            filter: true,
            headerName: 'Role',
          },
          {
            filter: true,
            field: 'electionDayLocation.locationName',
            headerName: 'Location',
          },
          {
            filter: true,
            headerName: 'Location Address',
            columnGroupShow: 'open',
            valueGetter: (params) => {
              return [
                params.data.electionDayLocation?.address1,
                params.data.electionDayLocation?.address2,
                params.data.electionDayLocation?.state,
                params.data.electionDayLocation?.city,
                params.data.electionDayLocation?.zip,
              ]
                .filter((p) => !!p)
                .join(', ');
            },
          },
          {
            columnGroupShow: 'closed',
            filter: true,
            headerName: 'Precinct',
            valueGetter: (params: any) => {
              const precinctInfo = params.data.electionDayPrecinct;
              return [precinctInfo?.precinctCode, precinctInfo?.precinctName].filter((p) => !!p).join(' - ');
            },
          },
          {
            columnGroupShow: 'open',
            field: 'electionDayPrecinct.precinctCode',
            filter: true,
            headerName: 'Precinct Code',
          },
          {
            columnGroupShow: 'open',
            field: 'electionDayPrecinct.precinctName',
            filter: true,
            headerName: 'Precinct Name',
          },
        ],
      },
      {
        field: 'assignedToClass',
        width: 130,
        filter: true,
        enableRowGroup: true,
        headerName: 'Training',
        cellDataType: 'boolean',
      },
      {
        field: 'pollworkerInfo.party',
        filter: true,
        enableRowGroup: true,
        width: 100,
        headerName: 'Party',
        filterValueGetter: (params: any) => {
          const value = params.data.pollworkerInfo.party;
          const party = parties.find((p) => value === p.partyCode);
          return party ? party.partyName : value || 'Unspecified';
        },
        getQuickFilterText: (params) => {
          const value = params.value ? params.value : '';
          const party = parties.find((p) => params.value === p.partyCode);
          return party ? party.partyName : value || 'Unspecified';
        },
        valueFormatter: (params: any) => {
          const value = params.value ? params.value : '';
          const party = parties.find((p) => params.value === p.partyCode);
          return party ? party.partyName : value;
        },
        editable: true,
        context: {
          update: { type: 'pollworkerInfo' },
        },
        cellEditorSelector: () => {
          return {
            component: 'agRichSelectCellEditor',
            params: {
              values: async () => {
                if (!moduleInfo?.Election) return;

                return parties.map((p: Party) => p.partyCode);
              },
              cellRenderer: (params: any) => {
                const party = parties.find((p) => p.partyCode === params.value);

                if (!party) return null;

                return (
                  <div className="w-full space-x-2">
                    <span className="inline-block w-[2em]">{party.partyCode}</span>
                    <span className="inline-block capitalize">{party.partyName}</span>
                  </div>
                );
              },
            },
            allowTyping: true,
            popup: true,
            cellHeight: 50,
          };
        },
      },
      {
        field: 'pollworkerInfo.registeredPrecinctInfo.precinctName',
        filter: true,
        headerName: 'Registered Precinct',
        enableRowGroup: true,
        editable: true,
        context: {
          update: {
            type: 'pollworkerInfo',
            isKeyUpdate: true,
            keyPathToUpdate: 'keyRegisteredPrecinctId',
            getValue: (pollworker: PollworkerWorkHistory, newValue: string) => {
              const newPrecinct = precincts.find((p) => p.precinctName === newValue);

              if (!newPrecinct) return pollworker.pollworkerInfo.registeredPrecinctInfo.keyPrecinctId;

              return { keyRegisteredPrecinctId: newPrecinct.id };
            },
          },
        },
        cellEditorSelector: () => {
          return {
            component: 'agRichSelectCellEditor',
            params: {
              values: () => {
                return precincts.map((p: Precinct) => p.precinctName);
              },
            },
            allowTyping: true,
            popup: true,
            cellHeight: 50,
          };
        },
      },
      {
        field: '_reportsTo.id',
        filter: true,
        headerName: 'Reports To',
        enableRowGroup: true,
        editable: reportsTo.length > 0,
        context: {
          update: { type: 'roleInfo2' },
        },
        valueGetter: (params: any) => {
          const report = reportsToById[params.data?._reportsTo?.id];
          if (!report) return null;
          return `${report.firstName} ${report.lastName}`;
        },
        valueFormatter: (params: any) => params.value,

        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: reportsTo.map((rt) => rt.id),
          cellRenderer: (params: any) => {
            const reportTo = reportsTo.find((rt) => rt.id === params.value);
            if (!reportTo) return null;
            return (
              <div className="inline-block capitalize">
                {reportTo.firstName} {reportTo.lastName}
              </div>
            );
          },
          filterList: true,
          searchType: 'match',
          allowTyping: true,
        } as IRichCellEditorParams,
      },
      {
        field: 'pollworkerInfo.notes',
        editable: true,
        filter: true,
        headerName: 'Notes',
        context: {
          update: { type: 'pollworkerInfo' },
        },
      },
      {
        field: 'pollworkerInfo.userInfo.employeeNumber',
        filter: true,
        headerName: !!account ? account.pollworkerEmployeeIdDisplayName : 'Employee #',
      },
      {
        field: 'pollworkerInfo.userInfo.voterRegistrationNumber',
        filter: true,
        headerName: 'VR #',
        editable: true,
        context: {
          update: { type: 'userInfo' },
        },
      },
      {
        field: 'pollworkerInfo.onlineLearningId',
        filter: true,
        headerName: 'Online Learning ID',
        editable: true,
        context: {
          update: { type: 'pollworkerInfo' },
        },
      },
      {
        headerName: 'Personal Info',
        children: [
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.prefix',
            filter: true,
            editable: true,
            headerName: 'Prefix',
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.firstName',
            editable: true,
            filter: true,
            headerName: 'First Name',
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.middleName',
            editable: true,
            filter: true,
            headerName: 'Middle Name',
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.lastName',
            editable: true,
            filter: true,
            headerName: 'Last Name',
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.suffix',
            filter: true,
            editable: true,
            headerName: 'Suffix',
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            columnGroupShow: 'closed',
            headerName: 'Full Name',
            filter: true,
            colId: 'fullName',
            enableRowGroup: true,
            editable: false,
            enableCellChangeFlash: true,
            valueGetter: (params: any) => {
              const { pollworkerInfo } = params.data;

              return [
                pollworkerInfo.userInfo.prefix,
                pollworkerInfo.userInfo.firstName,
                pollworkerInfo.userInfo.middleName,
                pollworkerInfo.userInfo.lastName,
                pollworkerInfo.userInfo.suffix,
              ]
                .filter((n) => !!n)
                .join(' ');
            },
          },
          {
            field: 'pollworkerInfo.userInfo.homePhone',
            headerName: 'Home Phone',
            editable: true,
            valueFormatter: phoneNumberFormatter,
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            field: 'pollworkerInfo.userInfo.cellPhone',
            headerName: 'Cell Phone',
            editable: true,
            valueFormatter: phoneNumberFormatter,
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            field: 'pollworkerInfo.userInfo.emailAddress',
            filter: true,
            headerName: 'Email Address',
            editable: true,
            context: {
              update: { type: 'userInfo' },
            },
            valueGetter: (params: any) => {
              if (params.node.group) return null;

              return params.data.pollworkerInfo.userInfo.emailAddress;
            },
            valueSetter: (params: any) => {
              params.data.pollworkerInfo.userInfo.emailAddress = params.newValue;
              return validator.isEmail(params.newValue);
            },
          },
          {
            field: 'pollworkerInfo.dateOfBirth',
            editable: true,
            filter: true,
            headerName: 'Date Of Birth',
            cellDataType: 'date',
            valueFormatter: dateFormatter,
            context: {
              update: {
                type: 'pollworkerInfo',
                isKeyUpdate: true,
                getValue: (pollworker: PollworkerWorkHistory, newValue: any) => {
                  return {
                    dateOfBirth: dayjs(newValue).toISOString(),
                  };
                },
              },
            },
          },
          {
            field: 'pollworkerInfo.gender',
            filter: true,
            enableRowGroup: true,
            headerName: 'Gender',
            editable: true,
            context: {
              update: { type: 'pollworkerInfo' },
            },
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: ['U', 'M', 'F', ''],
              cellRenderer: (params: any) => {
                return (
                  <div>
                    {params.value === 'U' ? 'Unspecified' : null}
                    {params.value === 'M' ? 'Male' : null}
                    {params.value === 'F' ? 'Female' : null}
                    {params.value === '' || params.value === null ? 'Other' : null}
                  </div>
                );
              },
            },
          },
        ],
      },
      {
        headerName: 'Mailing Address',
        children: [
          {
            headerName: 'Full Address',
            enableCellChangeFlash: true,
            valueGetter: (params: any) => {
              return [
                params.data.pollworkerInfo.userInfo.address1,
                params.data.pollworkerInfo.userInfo.address2,
                params.data.pollworkerInfo.userInfo.state,
                params.data.pollworkerInfo.userInfo.city,
                params.data.pollworkerInfo.userInfo.zip,
              ]
                .filter((p) => !!p)
                .join(', ');
            },
            width: 300,
            columnGroupShow: 'closed',
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.address1',
            headerName: 'Address 1',
            editable: true,
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.address2',
            headerName: 'Address 2',
            editable: true,
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.city',
            headerName: 'City',
            enableRowGroup: true,
            editable: true,
            context: {
              update: { type: 'userInfo' },
            },
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.state',
            headerName: 'State',
            enableRowGroup: true,
            editable: true,
            context: {
              update: { type: 'userInfo' },
            },
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: Object.keys(STATES_BY_ABBR),
              cellRenderer: (params: any) => (
                <div className="w-full space-x-2">
                  <span className="inline-block w-[2em]">{STATES_BY_ABBR[params.value].abbr}</span>
                  <span className="inline-block capitalize">{STATES_BY_ABBR[params.value].state.toLowerCase()}</span>
                </div>
              ),
              filterList: true,
              searchType: 'match',
              allowTyping: true,
            } as IRichCellEditorParams,
          },
          {
            columnGroupShow: 'open',
            field: 'pollworkerInfo.userInfo.zip',
            headerName: 'Zip',
            enableRowGroup: true,
            editable: true,
            context: {
              update: { type: 'userInfo' },
            },
          },
        ],
      },
      {
        headerName: 'Custom Fields',
        children: customFieldsByName.map((key: string) => {
          let dataType;
          const props: Partial<ColDef> = {};

          // TODO: Optimize this
          switch (customFieldsByPromptText[key].dataTypeId) {
            case 4:
              dataType = 'date';
              props.valueFormatter = dateFormatter;
              break;
            case 3:
              dataType = 'boolean';
              props.valueFormatter = (params: any) => {
                return params.value === true ? 'Yes' : 'No';
              };
              break;
            case 2:
              dataType = 'number';
              break;
            case 0:
            case 1:
            default:
              dataType = 'text';
          }

          return {
            field: `_customFields.${key}`,
            headerName: key,
            cellDataType: dataType,
            enableRowGroup: true,
            ...props,
          };
        }),
      },
      {
        field: 'dateAdded',
        filter: true,
        headerName: 'Date Added',
        cellDataType: 'date',
        valueFormatter: dateFormatter,
      },
    ];
  },
);

export const selectStaffingPartiesFromGrid = createSelector([partiesSelector], (parties): { parties: PartyConfig[]; requiresPartyBalance: boolean } => ({
  parties: parties.map((party) => ({
    id: party.id,
    partyCode: party.partyCode,
    partyName: party.partyName,
    color: PARTY_COLORS[party.partyCode] || generateHslColor(party.partyName),
  })),
  requiresPartyBalance: parties.length > 0,
}));

export const selectAvailableWorkersFromGrid = createSelector([pollworkers], (pw): PickerWorker[] => {
  if (!pw?.workerList) return [];

  return pw.workerList.map((worker) => ({
    evUserId: worker.keyEVUserId,
    firstName: worker.userInfo?.firstName || '',
    lastName: worker.userInfo?.lastName || '',
    partyCode: worker.pollworkerInfo?.party,
    rolePreference: worker.roleInfo?.roleName,
  }));
});

const allWorkersSelector = (state: AppState) => state.pollworker.grid.allWorkers;
export const loadingPollworkerDataSelector = (state: AppState) => state.pollworker.grid.loadingPollworkerData;

export const selectAllAvailableWorkers = createSelector([allWorkersSelector], (allWorkers): PickerWorker[] => {
  if (!allWorkers?.length) return [];

  return allWorkers.map((worker) => ({
    evUserId: worker.keyEVUserId,
    firstName: worker.userInfo?.firstName || '',
    lastName: worker.userInfo?.lastName || '',
    partyCode: worker.pollworkerInfo?.party,
    rolePreference: worker.roleInfo?.roleName,
    locationPreference: worker.defaultLocationInfo?.locationName,
    defaultRoleId: worker.roleInfo?.id,
    defaultRoleName: worker.roleInfo?.roleName,
    defaultLocationId: worker.defaultLocationInfo?.id,
    defaultLocationName: worker.defaultLocationInfo?.locationName,
    email: worker.pollworkerInfo?.userInfo?.emailAddress,
    earlyVotingWorker: worker.pollworkerInfo?.earlyVotingWorker ?? false,
    electionDayWorker: worker.pollworkerInfo?.electionDayWorker ?? false,
  }));
});

export const selectIsMasterList = createSelector(
  (state: AppState) => state.pollworker?.moduleInfo?.Election,
  (election) => !election,
);
